// Components/DesignationProtectedRoute.js
import React, { useContext, useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import AuthContext from '../Components/AuthContext';
import api from '../services/api';
import { Box, CircularProgress, Alert, Typography, Button } from '@mui/material';

/**
 * A protected route component that checks if the user has a specific designation.
 *
 * @param {Object} props
 * @param {React.ReactNode} props.children - The children to render if access is granted
 * @param {string|string[]} props.allowedDesignations - The designation(s) required to access this route
 */
const DesignationProtectedRoute = ({ children, allowedDesignations }) => {
  const { isLoggedIn, loading: authLoading } = useContext(AuthContext);
  const [userDesignation, setUserDesignation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [hasAccess, setHasAccess] = useState(false);

  useEffect(() => {
    const fetchUserProfile = async () => {
      if (!isLoggedIn) {
        setLoading(false);
        return;
      }

      try {
        const response = await api.get('users/profile/', {
          headers: { Authorization: `Token ${localStorage.getItem('Token')}` },
        });

        const designation = response.data.designation;
        const isAdmin = response.data.is_admin === true;
        setUserDesignation(designation);

        // Admins bypass designation checks
        if (isAdmin) {
          setHasAccess(true);
          return;
        }

        // Check if user's designation is in the allowed list
        const designationsArray = Array.isArray(allowedDesignations)
          ? allowedDesignations
          : [allowedDesignations];

        const access = designationsArray.some(
          (allowed) => designation?.toLowerCase() === allowed?.toLowerCase()
        );
        setHasAccess(access);
      } catch (err) {
        console.error('Error fetching user profile:', err);
        setError('Failed to verify access permissions');
      } finally {
        setLoading(false);
      }
    };

    if (!authLoading) {
      fetchUserProfile();
    }
  }, [isLoggedIn, authLoading, allowedDesignations]);

  // Wait for auth loading to complete
  if (authLoading || loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  // Redirect to login if not authenticated
  if (!isLoggedIn) {
    return <Navigate to="/login" replace />;
  }

  // Show error if there was a problem
  if (error) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
          flexDirection: 'column',
          gap: 2,
        }}
      >
        <Alert severity="error">{error}</Alert>
        <Button variant="contained" onClick={() => window.location.reload()}>
          Retry
        </Button>
      </Box>
    );
  }

  // Show access denied if user doesn't have the required designation
  if (!hasAccess) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
          flexDirection: 'column',
          gap: 2,
          p: 3,
        }}
      >
        <Alert severity="warning" sx={{ maxWidth: 500 }}>
          <Typography variant="h6" gutterBottom>
            Access Restricted
          </Typography>
          <Typography variant="body2">
            This page is only accessible to users with the designation:{' '}
            <strong>
              {Array.isArray(allowedDesignations)
                ? allowedDesignations.join(' or ')
                : allowedDesignations}
            </strong>
          </Typography>
          <Typography variant="body2" sx={{ mt: 1 }}>
            Your current designation: <strong>{userDesignation || 'Not set'}</strong>
          </Typography>
        </Alert>
        <Button variant="contained" onClick={() => window.history.back()}>
          Go Back
        </Button>
      </Box>
    );
  }

  // Render children if access is granted
  return children;
};

export default DesignationProtectedRoute;
